import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Menu, X, User, BarChart3, LogOut, RefreshCw, ChevronDown, Globe } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AuthModal from "./AuthModal";

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'zh-CN', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'sw', name: 'Swahili', flag: '🇰🇪' },
];

const LanguageSelector = () => {
  const gtRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(LANGUAGES[0]);
  const dropdownRef = useRef(null);

  // Load Google Translate script once
  useEffect(() => {
    if (!document.querySelector('script[src*="translate.google.com"]')) {
      window._gtCallback = () => {
        if (gtRef.current) return;
        const div = document.createElement('div');
        div.id = '_gt_hidden';
        div.style.display = 'none';
        document.body.appendChild(div);
        try {
          new window.google.translate.TranslateElement({
            pageLanguage: 'en',
            includedLanguages: LANGUAGES.map(l => l.code).join(','),
            autoDisplay: false,
          }, '_gt_hidden');
          gtRef.current = true;
        } catch (e) {}
      };
      const s = document.createElement('script');
      s.src = 'https://translate.google.com/translate_a/element.js?cb=_gtCallback';
      s.async = true;
      document.head.appendChild(s);
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectLanguage = (lang) => {
    setCurrent(lang);
    setOpen(false);
    // Trigger Google Translate via the hidden combo
    const combo = document.querySelector('._gt_hidden select.goog-te-combo, #_gt_hidden select.goog-te-combo, select.goog-te-combo');
    if (combo) {
      combo.value = lang.code;
      combo.dispatchEvent(new Event('change'));
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-lg px-3 py-1.5 transition-all"
        data-testid="language-selector"
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="text-white/80 text-sm font-medium hidden sm:inline">{current.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl shadow-black/40 py-1.5 z-[9999] min-w-[180px] max-h-[320px] overflow-y-auto" data-testid="language-dropdown">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => selectLanguage(lang)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.08] transition-colors ${current.code === lang.code ? 'bg-purple-500/10 text-purple-400' : 'text-white/70'}`}
              data-testid={`lang-${lang.code}`}
            >
              <span className="text-lg leading-none">{lang.flag}</span>
              <span className="text-sm font-medium">{lang.name}</span>
              {current.code === lang.code && <span className="ml-auto text-purple-400 text-xs">&#10003;</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = ({ onOpenAuth }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [localAuthMode, setLocalAuthMode] = useState(null);
  const { user, logout, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleOpenAuth = (mode) => {
    if (onOpenAuth) {
      onOpenAuth(mode);
    } else {
      setLocalAuthMode(mode);
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '#home' },
    { label: 'Features', href: '#features' },
    { label: 'Artists', href: '#artists' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Section not on this page — navigate to landing page with hash
      navigate('/' + href);
    }
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
  };

  return (
    <>
    <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-xl shadow-lg' : 'bg-transparent'}`} data-testid="header">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <button onClick={() => scrollToSection('#home')} className="flex items-center" data-testid="logo">
            <img src="/logo-header.png" alt="DJsandMCMedia™" className="h-8 w-auto" />
          </button>

          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <button key={link.href} onClick={() => scrollToSection(link.href)}
                className="text-white/70 hover:text-white transition text-sm font-medium" data-testid={`nav-${link.label.toLowerCase()}`}>
                {link.label}
              </button>
            ))}
            <a href="/news" className="text-white/70 hover:text-white transition text-sm font-medium" data-testid="nav-news">News</a>
            <a href="/shop" className="text-white/70 hover:text-white transition text-sm font-medium" data-testid="nav-shop">Shop</a>

            {/* More Dropdown */}
            <div className="relative">
              <button onClick={() => setMoreOpen(!moreOpen)}
                className="flex items-center gap-1 text-white/70 hover:text-white transition text-sm font-medium" data-testid="nav-more-btn">
                More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
              </button>
              {moreOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setMoreOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-44 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-xl overflow-hidden z-40" data-testid="more-dropdown">
                    <div className="py-1">
                      {[
                        { href: '/discover', label: 'Discover' },
                        { href: '/directory', label: 'Radio Directory' },
                        { href: '/network', label: 'Showcase' },
                        { href: '/submit', label: 'Submit Music' },
                        { href: '/artists', label: 'Artists' },
                        { href: '/affiliate', label: 'Affiliate Program' },
                        { href: '/advertise', label: 'Advertise With Us' },
                        { href: '/about', label: 'About the Owner' },
                        { href: '/why-us', label: 'Why Us' },
                        { href: '/guide', label: 'Setup Guide' },
                      ].map((item) => (
                        <a key={item.href} href={item.href} onClick={() => setMoreOpen(false)}
                          className="block px-4 py-2.5 text-white/70 hover:text-white hover:bg-white/5 transition text-sm" data-testid={`nav-more-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                          {item.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector />
            <button
              data-testid="refresh-btn"
              onClick={() => window.location.reload()}
              className="text-white/40 hover:text-white/80 p-2 rounded-lg hover:bg-white/5 transition"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            {user ? (
              <div className="relative">
                <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl transition" data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center overflow-hidden">
                    {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-4 h-4 text-white" />}
                  </div>
                  <span className="text-white text-sm font-medium">{user.name}</span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-gray-900 border border-white/10 rounded-xl shadow-xl overflow-hidden" data-testid="user-dropdown">
                    <div className="p-4 border-b border-white/10">
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-white/50 text-sm">{user.email}</p>
                      <p className="text-purple-400 text-xs mt-1 capitalize">{user.plan === 'premium' ? 'Premium' : user.plan} Plan</p>
                    </div>
                    <div className="p-2">
                      <button onClick={() => { navigate('/dashboard'); setUserMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition text-sm">
                        <BarChart3 className="w-4 h-4" /> Dashboard
                      </button>
                      <button onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-white/5 rounded-lg transition text-sm" data-testid="logout-btn">
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <a href="/login" className="text-white/70 hover:text-white transition text-sm font-medium" data-testid="login-btn">Login</a>
                <a href="/login?mode=register" className="bg-purple-500 hover:bg-purple-600 text-white px-5 py-2 rounded-xl transition text-sm font-semibold" data-testid="signup-btn">Sign Up</a>
              </>
            )}
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-white p-2" data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 top-0 left-0 right-0 bottom-0 z-50 bg-[#0a0a0a] overflow-y-auto" data-testid="mobile-menu">
            <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
              <a href="/" className="flex items-center gap-2">
                <img src="/logo-header.png" alt="DJsandMCMedia" className="h-8" onError={(e) => { e.target.style.display = 'none'; }} />
                <span className="text-white font-bold text-lg">DJsandMCMedia<sup className="text-[8px] text-white/40">TM</sup></span>
              </a>
              <button onClick={() => setMobileMenuOpen(false)} className="text-white p-2" data-testid="mobile-menu-close">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 px-6 py-4">
              {navLinks.map((link) => (
                <button key={link.href} onClick={() => scrollToSection(link.href)} className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]">{link.label}</button>
              ))}
              <a href="/discover" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>Discover</a>
              <a href="/directory" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>Radio Directory</a>
              <a href="/network" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>Showcase</a>
              <a href="/submit" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>Submit Music</a>
              <a href="/artists" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>Artists</a>
              <a href="/affiliate" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>Affiliate Program</a>
              <a href="/about" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>About the Owner</a>
              <a href="/why-us" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>Why Us</a>
              <a href="/news" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>News</a>
              <a href="/shop" className="text-white/70 hover:text-white transition py-3 text-left text-base border-b border-white/[0.04]" onClick={() => setMobileMenuOpen(false)}>Shop</a>
              <div className="my-3 pt-2">
                <LanguageSelector />
              </div>
              {user ? (
                <>
                  <div className="border-t border-white/10 my-2 pt-3">
                    <p className="text-white font-medium">{user.name}</p>
                    <p className="text-white/50 text-sm">{user.email}</p>
                  </div>
                  <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="text-purple-400 py-3 text-left text-base">Dashboard</button>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="text-red-400 py-3 text-left text-base">Sign Out</button>
                </>
              ) : (
                <div className="flex gap-3 mt-4 pt-4 border-t border-white/10">
                  <a href="/login" className="flex-1 bg-white/10 text-white py-3 rounded-xl text-sm font-medium text-center" onClick={() => setMobileMenuOpen(false)}>Login</a>
                  <a href="/login?mode=register" className="flex-1 bg-purple-500 text-white py-3 rounded-xl text-sm font-medium text-center" onClick={() => setMobileMenuOpen(false)}>Sign Up</a>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
    {localAuthMode && (
      <AuthModal isOpen={true} mode={localAuthMode} onClose={() => setLocalAuthMode(null)} onSwitchMode={(m) => setLocalAuthMode(m)} />
    )}
    </>
  );
};


export default Header;
